import React, { useState, useEffect, useCallback } from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import ListSubheader from '@mui/material/ListSubheader';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { usePersistedDataStore } from '../../store';
import logger from '../../../utils/logger';

const BadDataFlagsSelector = ({ 
  uniqueFields, 
  dataSelectedCount, 
  fieldDisplayName,
  // Optional props for external state management
  externalBadDataFlags = null,
  onBadDataFlagsChange = null,
  externalFlagType = null,
  onFlagTypeChange = null,
  // Whether to sync with global store (default: true for backward compatibility)
  syncWithStore = true,
  // External resetting state (for data grid usage)
  isResetting: externalIsResetting = null
}) => {
  // Global store state (only used if syncWithStore is true)
  const globalBadDataFlags = usePersistedDataStore((state) => state.badDataFlags);
  const setGlobalBadDataFlags = usePersistedDataStore((state) => state.setBadDataFlags);
  const globalFlagType = usePersistedDataStore((state) => state.flagType);
  const setGlobalFlagType = usePersistedDataStore((state) => state.setFlagType);

  // Local state management
  const [localBadDataFlags, setLocalBadDataFlags] = useState({
    data: [],
    outliers: []
  });
  const [localFlagType, setLocalFlagType] = useState('data');
  
  // Loading state for municipality changes
  const [internalIsResetting, setInternalIsResetting] = useState(false);
  const [previousBadDataFlags, setPreviousBadDataFlags] = useState(null);
  const [isUserChange, setIsUserChange] = useState(false);
  
  // Use external resetting state if provided, otherwise use internal state
  const isResetting = externalIsResetting !== null ? externalIsResetting : internalIsResetting;

  // Determine which state to use based on syncWithStore and external props
  const badDataFlags = externalBadDataFlags || (syncWithStore ? globalBadDataFlags : localBadDataFlags);
  const setBadDataFlags = onBadDataFlagsChange || (syncWithStore ? setGlobalBadDataFlags : setLocalBadDataFlags);
  const flagType = externalFlagType || (syncWithStore ? globalFlagType : localFlagType);
  const setFlagType = onFlagTypeChange || (syncWithStore ? setGlobalFlagType : setLocalFlagType);

  // Initialize local state from global store when component mounts
  useEffect(() => {
    if (syncWithStore && globalBadDataFlags) {
      setLocalBadDataFlags(globalBadDataFlags);
    }
    if (syncWithStore && globalFlagType) {
      setLocalFlagType(globalFlagType);
    }
  }, [syncWithStore, globalBadDataFlags, globalFlagType]);

  // Detect when bad data flags are being reset due to municipality changes
  useEffect(() => {
    if (syncWithStore && globalBadDataFlags) {
      // Check if this is a reset (flags changed significantly) AND it's not a user change
      if (previousBadDataFlags && 
          JSON.stringify(previousBadDataFlags) !== JSON.stringify(globalBadDataFlags) &&
          !isUserChange) {
        
        // Show resetting indicator
        setInternalIsResetting(true);
        
        // Hide the indicator after a short delay
        const timer = setTimeout(() => {
          setInternalIsResetting(false);
        }, 500);
        
        // Cleanup timer
        return () => clearTimeout(timer);
      }
      
      // Reset user change flag after processing
      if (isUserChange) {
        setIsUserChange(false);
      }
      
      // Update previous flags for next comparison
      setPreviousBadDataFlags(globalBadDataFlags);
    }
  }, [syncWithStore, globalBadDataFlags, previousBadDataFlags, isUserChange]);

  const handleFlagChange = useCallback((event) => {
    const newFlags = event.target.value;
    const updatedFlags = {
      ...badDataFlags,
      [flagType]: newFlags
    };
    setIsUserChange(true); // Mark this as a user change
    setBadDataFlags(updatedFlags);
  }, [badDataFlags, flagType, setBadDataFlags]);

  const handleTypeChange = useCallback((type) => {
    logger.log('handleTypeChange called with:', type);
    setFlagType(type);
  }, [setFlagType]);

  const handleSelectAll = useCallback(() => {
    logger.log('handleSelectAll called');
    const updatedFlags = {
      ...badDataFlags,
      [flagType]: [...uniqueFields]
    };
    setIsUserChange(true); // Mark this as a user change
    setBadDataFlags(updatedFlags);
  }, [badDataFlags, flagType, uniqueFields, setBadDataFlags]);

  const handleClearAll = useCallback(() => {
    logger.log('handleClearAll called');
    const updatedFlags = {
      ...badDataFlags,
      [flagType]: []
    };
    setIsUserChange(true); // Mark this as a user change
    setBadDataFlags(updatedFlags);
  }, [badDataFlags, flagType, setBadDataFlags]);

  // Get the current selected flags based on flag type
  const selectedFlags = badDataFlags[flagType] || [];
  
  // Check if "Return All" is selected (always read from data[] — outliers tab has its own selection)
  const isReturnAllSelected = selectedFlags.includes('Return All');
  const returnAllInData = (badDataFlags?.data || []).includes('Return All');

  useEffect(() => {
    if (!returnAllInData || !(badDataFlags?.outliers || []).length) return;
    setBadDataFlags({ ...badDataFlags, outliers: [] });
  }, [returnAllInData, badDataFlags, setBadDataFlags]);

  useEffect(() => {
    if (returnAllInData && flagType === 'outliers') {
      setFlagType('data');
    }
  }, [returnAllInData, flagType, setFlagType]);

  return (
    <FormControl 
      sx={{ 
        minWidth: 200, 
        background: '#fff', 
        borderRadius: 1, 
        boxShadow: 1
      }}
    >
        <InputLabel shrink>Bad Data Flags</InputLabel>
      <Select
        multiple
        value={selectedFlags}
        onChange={handleFlagChange}
        label="Bad Data Flags"
        displayEmpty
        disabled={false} // Ensure it's never disabled
        renderValue={() => {
          if (isResetting) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Updating...</span>
                <CircularProgress size={16} />
              </Box>
            );
          }
          if (returnAllInData && flagType === 'data') {
            return 'Return all Data';
          }
          return dataSelectedCount > 0 ? `${dataSelectedCount} Fields Selected` : 'None selected';
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 1,
              boxShadow: 3,
              '& .MuiMenuItem-root': {
                minHeight: 32,
                py: 0.5,
                '&.Mui-selected, &.Mui-selected:hover': {
                  backgroundColor: 'transparent !important',
                },
                '&.selected-comp': {
                  backgroundColor: 'transparent !important',
                },
              },
            },
          },
        }}
        sx={{
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            py: 0.5,
            color: 'inherit',
            minHeight: 32,
            height: 32,
          }
        }}
      >
        {/* Data vs Outliers toggle */}
        <ListSubheader disableSticky sx={{ backgroundColor: 'transparent', px: 0.5, py: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Button
              variant="text"
              size="small"
              color={flagType === 'data' ? 'primary' : 'inherit'}
              sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 11, textTransform: 'none' }}
              onClick={(e) => {
                e.stopPropagation();
                handleTypeChange('data');
              }}
            >
              Data
            </Button>
            <Tooltip
              title="Cannot flag outliers when returning all data"
              disableHoverListener={!returnAllInData}
            >
              <Button
                variant="text"
                size="small"
                color={flagType === 'outliers' ? 'primary' : 'inherit'}
                aria-disabled={returnAllInData}
                sx={{
                  minWidth: 0,
                  px: 0.75,
                  py: 0,
                  fontSize: 11,
                  textTransform: 'none',
                  ...(returnAllInData && {
                    opacity: 0.38,
                    cursor: 'not-allowed',
                  }),
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (returnAllInData) return;
                  handleTypeChange('outliers');
                }}
              >
                Outliers
              </Button>
            </Tooltip>
          </Box>
        </ListSubheader>

        <Divider sx={{ my: 0.25 }} />

        {/* Return All option - only available for data */}
        {flagType === 'data' && (
          <>
            <MenuItem
              value="Return All"
              disableRipple
              onClick={(e) => {
                e.stopPropagation();
                // Toggle "Return All" selection
                if (isReturnAllSelected) {
                  // Remove "Return All" from selection
                  const newFlags = selectedFlags.filter(flag => flag !== 'Return All');
                  const updatedFlags = {
                    ...badDataFlags,
                    [flagType]: newFlags
                  };
                  setIsUserChange(true); // Mark this as a user change
                  setBadDataFlags(updatedFlags);
                } else {
                  const updatedFlags = {
                    ...badDataFlags,
                    data: ['Return All'],
                    outliers: [],
                  };
                  setIsUserChange(true);
                  setBadDataFlags(updatedFlags);
                }
              }}
              sx={{
                minHeight: 32,
                py: 0.5,
                backgroundColor: isReturnAllSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                '&.Mui-selected, &.Mui-selected:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08) !important',
                },
              }}
            >
              <Checkbox
                checked={isReturnAllSelected}
                size="small"
                sx={{ p: 0.5 }}
              />
              <ListItemText
                primary="Return all Data"
                primaryTypographyProps={{ 
                  fontSize: 14,
                  fontWeight: isReturnAllSelected ? 600 : 400,
                  color: isReturnAllSelected ? 'primary.main' : 'inherit'
                }}
                sx={{ my: 0 }}
              />
            </MenuItem>
            <Divider sx={{ my: 0.25 }} />
          </>
        )}

        {uniqueFields.map((field) => (
          <MenuItem
            key={field}
            value={field}
            disableRipple
            sx={{
              minHeight: 32,
              py: 0.5,
              '&.Mui-selected, &.Mui-selected:hover': {
                backgroundColor: 'transparent !important',
              },
              '&.selected-comp': {
                backgroundColor: 'transparent !important',
              },
            }}
          >
            <Checkbox
              checked={selectedFlags.includes(field)}
              size="small"
              sx={{ p: 0.5 }}
            />
            <ListItemText
              primary={fieldDisplayName ? fieldDisplayName(field) : (field === 'PhotoURL' ? 'Photo' : field)}
              primaryTypographyProps={{ fontSize: 14 }}
              sx={{ my: 0 }}
            />
          </MenuItem>
        ))}

        <Divider sx={{ my: 0.5 }} />

        <MenuItem disableRipple disableTouchRipple sx={{ minHeight: 28, py: 0, px: 0.5, '&:hover': { backgroundColor: 'transparent' } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <Button
              variant="text"
              color="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleSelectAll();
              }}
              sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11 }}
            >
              Select All
            </Button>
            <Button
              variant="text"
              color="error"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11 }}
            >
              Clear All
            </Button>
          </Box>
        </MenuItem>
      </Select>
    </FormControl>
  );
};

export default BadDataFlagsSelector; 